
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import '../../../styles/test-cases-modal.css';
import axios from 'axios';
import axiosInstance from '../../../api/axiosInstance';
// import { toast } from 'react-toastify'; // Commented out - using react-toast now
import { toast } from 'react-toast';
import { Pagination } from '../../ui';
const API_BASE_URL = process.env.REACT_APP_BASE_URL;
 const TestCasesModal = ({ 
  testCasesData, 
  onClose, 
  onViewTestCases, 
  title = "Generated Test Cases",
  projectId,
  storyId,
  isServerSide = false 
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [serverTestCases, setServerTestCases] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [openTestCaseId, setOpenTestCaseId] = useState(null); // single-open collapsible
  const initialDataUsedRef = useRef(false);
  const contentRef = useRef(null);
  const [portalTarget, setPortalTarget] = useState(() =>
    typeof document !== 'undefined'
      ? document.querySelector('[data-rt-shell-main]') || document.body
      : null
  );

  useEffect(() => {
    const shellMain = document.querySelector('[data-rt-shell-main]');
    setPortalTarget(shellMain || document.body);
    if (!shellMain) return undefined;
    shellMain.setAttribute('data-rt-shell-locked', '');
    const prevScroll = shellMain.scrollTop;
    shellMain.scrollTop = 0;
    return () => {
      shellMain.removeAttribute('data-rt-shell-locked');
      shellMain.scrollTop = prevScroll;
    };
  }, []);

  const fetchTestCases = async (page) => {
    try {
      setLoading(true);

      const response = await axiosInstance.get(
        `/test_case/cases_list?project_id=${projectId}&page=${page}&page_size=${pageSize}&jira_story_id=${storyId}`
      );

      if (response.status === 200 || response.status === "success") {
        setServerTestCases(response);
      }
    } catch (error) {
      console.error('Error fetching test cases:', error);
      toast.error(`Failed to fetch test cases: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isServerSide || !projectId || !storyId) return;

    if (
      !initialDataUsedRef.current &&
      testCasesData?.data &&
      (testCasesData.page || 1) === currentPage
    ) {
      setServerTestCases(testCasesData);
      initialDataUsedRef.current = true;
      return;
    }

    fetchTestCases(currentPage);
  }, [currentPage, pageSize, projectId, storyId, isServerSide]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {

  if (!isServerSide && testCasesData) {
    setServerTestCases(testCasesData);
  }
}, [testCasesData, isServerSide]);

  // Open first test case by default when data loads
  useEffect(() => {
    const displayData = isServerSide ? serverTestCases : (serverTestCases || testCasesData);
    if (!displayData) return;

    // Use prefixed ids to distinguish scenario vs test ids
    if (displayData.scenarios && displayData.scenarios.length > 0) {
      const firstScenario = displayData.scenarios[0];
      const firstTest = firstScenario.generated_test_cases && firstScenario.generated_test_cases[0];
      const id = firstTest ? `test-${firstTest.test_case_id || firstTest.id}` : `scenario-${firstScenario.scenario_id}`;
      setOpenTestCaseId(id);
    } else if (displayData.data && displayData.data.length > 0) {
      const firstTestCase = displayData.data[0];
      const id = `test-${firstTestCase.test_case_id || firstTestCase.id}`;
      setOpenTestCaseId(id);
    } else {
      setOpenTestCaseId(null);
    }
  }, [serverTestCases, testCasesData, isServerSide]);

  // Toggle only at scenario level. Pass scenario id (prefixed) to open/close scenario
  const handleToggleTestCaseOpen = (id) => {
    setOpenTestCaseId(prev => (prev === id ? null : id));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

const handleRegenerateTestCases = async () => {
  if (!projectId || !storyId) {
    toast.error('Project ID or Story ID is missing');
    return;
  }

  try {
    setRegenerating(true);
    
    const authDataString = localStorage.getItem('user');
    if (!authDataString) {
      throw new Error('User not authenticated');
    }
    
    const authData = JSON.parse(authDataString);
    const accessToken = authData.accessToken;
    if (!accessToken) {
      throw new Error('Access token not found');
    }

    const config = {
      headers: {
        'token': accessToken,
        'Content-Type': 'application/json'
      }
    };

    const response = await axios.post(
      `${API_BASE_URL}/test_case/regen_scenario/story?project_id=${projectId}&story_id=${storyId}`,
      {},
      config
    );

    if (response.status === 200 || response.status === 201) {
      toast.success(response.data.message || 'Test cases regenerated successfully!');
       if (isServerSide) {
  
        await fetchTestCases(currentPage);
      } else {
       
        if (response.data.scenarios) {
       
          setServerTestCases(response.data);
        } else {
    
          setServerTestCases(response.data);
        }
      }
    }
  } catch (error) {
    console.error('Error regenerating test cases:', error);
    if (error.response) {
      toast.error(`Failed to regenerate test cases: ${error.response.data?.detail || error.response.statusText}`);
    } else if (error.request) {
      toast.error('Failed to regenerate test cases: No response from server');
    } else {
      toast.error(`Failed to regenerate test cases: ${error.message}`);
    }
  } finally {
    setRegenerating(false);
  }
};
 
 const displayData = isServerSide ? serverTestCases : (serverTestCases || testCasesData);
  const isFromScenarios = displayData?.scenarios !== undefined;
  const isFromTestCases = displayData?.data !== undefined;
  const effectivePageSize = displayData?.page_size || pageSize;
  const totalPages = displayData
    ? Math.max(1, Math.ceil((displayData.total || 0) / effectivePageSize) || 1)
    : 1;
  const showPagination = isServerSide && displayData && (displayData.total || 0) > 0;

  if (!displayData && !isServerSide) return null;
  if (!portalTarget) return null;

  return createPortal(
    <div data-rt-modal-overlay className="test-cases-modal-overlay" onClick={onClose}>
      <div className="test-cases-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* <div className="modal-header">
          <h2>{title}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div> */}
                <button 
            className="back-btn"
            onClick={onClose}
            title="Back to Scenarios"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back
          </button>
        <div className="test-cases-modal-content" ref={contentRef}>
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading test cases...</p>
            </div>
          ) : isFromScenarios ? (
          
            <>
              <div className="test-cases-summary">
                <p><strong>Story ID:</strong> {displayData.story_id}</p>
                <p><strong>Total Scenarios:</strong> {displayData.scenarios?.length || 0}</p>
                <p><strong>Total Test Cases:</strong> {displayData.scenarios?.reduce((total, scenario) => total + (scenario.generated_test_cases?.length || 0), 0) || 0}</p>
              </div>

              {displayData.scenarios?.map((scenario, sIndex) => {
                const scenarioKey = `scenario-${scenario.scenario_id || sIndex}`;
                const isScenarioOpen = openTestCaseId === scenarioKey;
                return (
                  <div key={scenarioKey} className={`scenario-test-cases ${isScenarioOpen ? 'open' : 'collapsed'}`}>
                    <h3 onClick={() => handleToggleTestCaseOpen(scenarioKey)} style={{cursor: 'pointer'}}>
                      Scenario {sIndex + 1}: {scenario.scenario_name}
                    </h3>

                    {isScenarioOpen && (
                      <>
                        <div className="scenario-details">
                          <p><strong>Type:</strong> {scenario.scenario_details?.scenario_type}</p>
                          <p><strong>Priority:</strong> {scenario.scenario_details?.priority}</p>
                          <p><strong>Summary:</strong> {scenario.scenario_details?.scenario_summary}</p>
                        </div>

                        <h4>Generated Test Cases:</h4>
                        <div className="test-cases-list">
                          {scenario.generated_test_cases?.map((testCase, tcIndex) => {
                            const testKey = `test-${testCase.test_case_id || testCase.id || tcIndex}`;
                            // Render each test case fully expanded when the parent scenario is open
                            return (
                              <div key={testKey} className={`test-case-item open`}>
                                <div>
                                  <h5>{testCase.name}</h5>
                                  <p><strong>Priority:</strong> {testCase.priority}</p>
                                  <p><strong>Description:</strong> {testCase.description}</p>
                                </div>

                                <div className="test-case-details">
                                  <h6>Prerequisites:</h6>
                                  <ul>
                                    {testCase.prerequisites?.map((prereq, prereqIndex) => (
                                      <li key={prereqIndex}>{prereq}</li>
                                    ))}
                                  </ul>

                                  <h6>Steps:</h6>
                                  <ol>
                                    {testCase.steps?.map((step, stepIndex) => (
                                      <li key={stepIndex}>{step}</li>
                                    ))}
                                  </ol>

                                  <h6>Expected Results:</h6>
                                  <ul>
                                    {testCase.expected_results?.map((result, resultIndex) => (
                                      <li key={resultIndex}>{result}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </>
          ) : isFromTestCases ? (
            
            <>
              <div className="test-cases-summary">
                <p><strong>Total Test Cases:</strong> {displayData.total}</p>
                <p><strong>Page:</strong> {displayData.page} of {Math.ceil(displayData.total / displayData.page_size)}</p>
                  <button 
      className="regenerate-btn"
      onClick={handleRegenerateTestCases}
      disabled={regenerating}
    >
      {regenerating ? (
        <>
          <div className="btn-spinner"></div>
          Regenerating...
        </>
      ) : (
        <>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 4v6h-6"></path>
            <path d="M1 20v-6h6"></path>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
            <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"></path>
          </svg>
          Regenerate
        </>
      )}
    </button>
              </div>

              {displayData.data?.map((testCase, index) => (
                <div key={testCase.test_case_id || index} className="test-case-item">
                  <h3>{testCase.name}</h3>
                  
                  <div className="test-case-meta">
                    <p><strong>ID</strong> {testCase.test_case_id}</p>
                    <p><strong>Priority</strong> {testCase.priority || '—'}</p>
                    <p><strong>Status</strong> {testCase.status || '—'}</p>
                    {testCase.environment ? (
                      <p><strong>Environment</strong> {testCase.environment}</p>
                    ) : null}
                    {testCase.browser ? (
                      <p><strong>Browser</strong> {testCase.browser}</p>
                    ) : null}
                    {testCase.device ? (
                      <p><strong>Device</strong> {testCase.device}</p>
                    ) : null}
                  </div>

                  {testCase.description ? (
                    <p className="test-case-desc">{testCase.description}</p>
                  ) : null}
                  
                  <div className="test-case-details">

                      <h4>Prerequisites:</h4>
                    <ul>
                      {testCase.steps?.prerequisites?.map((prereq, prereqIndex) => (
                        <li key={prereqIndex}>{prereq}</li>
                      ))}
                    </ul>
                    <h4>Steps:</h4>
                    <ol>
                      {testCase.steps?.step_description?.map((step, stepIndex) => (
                        <li key={stepIndex}>{step}</li>
                      ))}
                    </ol>
                    
                   

                    <h4>Expected Results:</h4>
                    <ul>
                      {testCase.steps?.expected_result?.map((result, resultIndex) => (
                        <li key={resultIndex}>{result}</li>
                      ))}
                    </ul>
                    
                   
                    
                    {testCase.steps?.additional_notes?.length > 0 && (
                      <>
                        <h4>Additional Notes:</h4>
                        <ul>
                          {testCase.steps?.additional_notes?.map((note, noteIndex) => (
                            <li key={noteIndex}>{note}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="no-test-cases">
              <p>No test cases data available.</p>
            </div>
          )}
        </div>
      

        {showPagination && (
          <div className="test-cases-modal-pagination">
            <Pagination
              page={displayData.page || currentPage}
              pageSize={effectivePageSize}
              total={displayData.total || 0}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 20, 50, 100]}
              loading={loading}
            />
          </div>
        )}

      
      </div>
    </div>,
    portalTarget
  );
};


export default TestCasesModal;